<template>
  <div>
    <div
      v-if="!isLoggedIn"
      data-test-id="auth-links"
    >
      <router-link
        class="md-button simple-link simple-link-white"
        tag="button"
        :md-ripple="false"
        :to="{ name: 'signup' }"
        data-test-id="auth-links-signup"
      >
        {{ i18n.lbl_signup() }}
      </router-link>
      <router-link
        class="md-button simple-link simple-link-white"
        tag="button"
        :md-ripple="false"
        :to="{ name: 'login' }"
        data-test-id="auth-links-login"
      >
        {{ i18n.lbl_signin() }}
      </router-link>
      {{ notificationMenuRefreshed() }}
    </div>

    <md-menu
      v-else
      @click="setClipboard()"
      :md-active.sync="isUserCardOpen"
      class="nav__user"
      md-align-trigger
      md-size="huge"
      md-direction="bottom-start"
      data-test-id="user-info"
    >
      <md-button
        md-menu-trigger
        :md-ripple="false"
        class="nav__user-triger-btn"
        data-test-id="user-info-toggle-btn"
      >
        <md-tooltip
          md-direction="bottom"
          :md-active.sync="tooltipActive"
          data-test-id="user-email-tooltip"
          class="custom-user-tooltip"
        >
          {{ userEmail }}
        </md-tooltip>
        <md-icon
          md-src="/static/img/user-2.svg"
          data-test-id="user-info-toggle-btn-icon"
        />
      </md-button>

      <md-menu-content
        class="nav__user-dropdown nav-dropdown redesign-menu"
        data-test-id="user-info-list-container"
      >
        <md-menu-item data-test-id="user-info-list-item">
          <md-button
            @click="showCopySuccess"
            class="md-icon-button"
            id="clipboard-btn"
            :data-clipboard-target="`#clipboard`"
            data-test-id="user-info-clipboard-btn"
          >
            <md-icon
              class="detail-row__clipboard-icon md-icon-size-065x"
              data-test-id="user-info-clipboard-btn-icon"
            >
              content_copy
            </md-icon>
          </md-button>

          <div class="nav-dropdown__info">
            <div
              class="nav-dropdown__email"
              data-test-id="user-info-email"
            >
              <span :id="`clipboard`">{{ userEmail }}</span>
            </div>
            <p
              v-if="!accountBlocked"
              class="nav-dropdown__status"
              data-test-id="user-info-status"
            >
              {{ accountStatus }}
            </p>
            <p
              v-else
              class="nav-dropdown__status"
              data-test-id="user-info-status"
            >
              {{ i18n.lbl_userBlocked() }}
            </p>
          </div>
        </md-menu-item>
        <md-menu-item
          class="nav-dropdown__actions"
          data-test-id="user-info-list-item"
        >
          <div class="btn-wrap space-between">
            <md-button
              class="btn btn_narrow md-primary md-mint"
              data-test-id="user-info-settings-btn"
              @click="goSettings"
            >
              {{ i18n.lbl_settings() }}
            </md-button>
            <md-button
              id="btnSignout"
              class="btn btn_narrow md-primary"
              data-test-id="user-info-logout-btn"
              @click="signOut"
            >
              {{ i18n.lbl_signout() }}
            </md-button>
          </div>
        </md-menu-item>
      </md-menu-content>
    </md-menu>
  </div>
</template>

<script>
  import { vuexTypes } from '@vuex/types'
  import { i18n } from '@js/i18n'
  import { mapGetters, mapActions } from 'vuex'
  import Clipboard from 'clipboard'
  import { EventDispatcher } from '@js/events/event_dispatcher'
  import { vueRoutes } from '@router/const'

  export default {
    name: 'UserInfo',

    data: () => ({
      i18n,
      isUserLoggedInPrevState: true,
      clipboard: null,
      isUserCardOpen: false,
      nextLogoutTime: null,
      sessionTimeoutMinutes: 30,
      logoutTimer: null,
      tooltipActive: false
    }),

    computed: {
      ...mapGetters({
        isLoggedIn: vuexTypes.isLoggedIn,
        userEmail: vuexTypes.userEmail,
        accountState: vuexTypes.accountState,
        accountBlocked: vuexTypes.accountBlocked,
        isProductionEnv: vuexTypes.isProductionEnv
      }),
      accountStatus () {
        return `
          ${this.i18n.lbl_account()}
          ${this.accountState === 'nil'
            ? this.i18n.lbl_not_verified()
            : this.i18n[this.accountState]()}`
      }
    },

    watch: {
      // следим за авторизацией
      isLoggedIn (newVal) {
        if (!this.isProductionEnv) return

        if (newVal) {
          this.startSessionWatcher()
        } else {
          this.stopSessionWatcher()
        }
      },

      tooltipActive (newVal) {
        if (newVal && this.isUserCardOpen) {
          this.$nextTick(() => {
            this.tooltipActive = false
          })
        }
      },

      isUserCardOpen (newVal) {
        if (newVal) this.tooltipActive = false
      }
    },

    methods: {
      ...mapActions({
        LOG_OUT: vuexTypes.LOG_OUT,
        GET_ACCOUNT_KYC_REQUESTS: vuexTypes.GET_ACCOUNT_KYC_REQUESTS,
        GET_UNREAD_MESSAGES: vuexTypes.GET_UNREAD_MESSAGES,
        GET_USER_LOCALE: vuexTypes.GET_USER_LOCALE,
        disconect: 'disconect',
        SET_HIDDEN_MENU_FORCED: 'SET_HIDDEN_MENU_FORCED'
      }),
      async loadLoggedInData () {
        await this.GET_ACCOUNT_KYC_REQUESTS()
        await this.GET_UNREAD_MESSAGES()
        await this.GET_USER_LOCALE()
      },
      notificationMenuRefreshed () {
        if (!this.isUserLoggedInPrevState && this.isLoggedIn) {
          this.loadLoggedInData()
        }

        this.isUserLoggedInPrevState = this.isLoggedIn
      },
      async setClipboard () {
        await this.$nextTick()
        this.clipboard = new Clipboard(document.querySelector('#clipboard-btn'))
      },
      showCopySuccess () {
        EventDispatcher.dispatchShowSuccessEvent(this.i18n.notif_success_copied())
        this.isUserCardOpen = false
      },
      closeUserCard () {
        this.isUserCardOpen = false
      },
      goSettings () {
        this.closeUserCard()
        this.$router.push(vueRoutes.settings)
      },
      async signOut () {
        this.closeUserCard()
        this.SET_HIDDEN_MENU_FORCED(true)
        await this.$nextTick()

        try {
          this.disconect()
        } finally {
          this.LOG_OUT()
        }
      },

      resetLogoutTime () {
        this.nextLogoutTime = Date.now() + this.sessionTimeoutMinutes * 60 * 1000
      },

      async checkLogout () {
        if (!this.isLoggedIn) return

        const remaining = this.nextLogoutTime - Date.now()
        if (remaining <= 0) {
          console.log('Автоматический logout...')
          await this.signOut()
          this.stopSessionWatcher()
        } else {
          this.logoutTimer = setTimeout(this.checkLogout, 1000)
        }
      },

      startSessionWatcher () {
        this.resetLogoutTime()
        document.addEventListener('mousemove', this.resetLogoutTime)
        document.addEventListener('keydown', this.resetLogoutTime)
        document.addEventListener('mousedown', this.resetLogoutTime)
        this.checkLogout()
      },

      stopSessionWatcher () {
        clearTimeout(this.logoutTimer)
        document.removeEventListener('mousemove', this.resetLogoutTime)
        document.removeEventListener('keydown', this.resetLogoutTime)
        document.removeEventListener('mousedown', this.resetLogoutTime)
        this.nextLogoutTime = null
      }
    },

    created () {
      if (this.isLoggedIn && this.isProductionEnv) {
        this.startSessionWatcher()
      }
    },

    beforeDestroy () {
      this.stopSessionWatcher()
    }
  }
</script>

<style scoped lang="scss">
  @import "../../scss/mixins";
  @import "../../scss/variables";

  .nav__user {
    position: relative;

    &-content {
      @include font('main', $font-size-m, 400);
    }

    &-triger-btn {
      min-width: 34px;
    }

    .md-button {
      &:before {
        content: none;
      }

      /deep/ .md-ripple {
        padding: 0;
      }

      /deep/ .md-button-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
    }
  }

  .nav-dropdown {
    max-width: 400px;
    max-height: unset;

    @include respond-to(small) {
      max-width: 350px;
    }

    .md-list {
      background-color: $color-white !important;
    }

    &__info {
      width: 100%;
      margin-bottom: 8px;
      margin-left: 8px;
    }

    &__email {
      display: flex;
      font-size: $font-size-l;

      @include respond-to(medium-tablet) {
        font-size: $font-size-m;
        word-break: break-word;
      }

      & #clipboard-btn {
        cursor: pointer;

        & i {
          font-size: 20px !important;
          height: 20px;
        }
      }
    }

    &__status {
      font-size: $font-size-s;
      text-align: left;
      white-space: normal;

      @include respond-to(small) {
        text-align: center;
      }
    }
  }

</style>



// WEBPACK FOOTER //
// src/vue/navigation/UserInfo.vue